// ☎️ CAL — API client drop-killer
// Wrapper fetch minimal. Credentials include siempre (cookie drop_session).
// Lanza APIError en !ok para que views lo atrapen y muestren toast.

export class APIError extends Error {
  constructor(status, body, msg) {
    super(msg || body?.error || `HTTP ${status}`)
    this.status = status
    this.body = body
  }
}

async function request(method, url, { body, headers, signal, query } = {}) {
  const qs = query ? '?' + new URLSearchParams(query).toString() : ''
  const opts = {
    method,
    credentials: 'include',
    headers: { 'Accept': 'application/json', ...(headers || {}) },
    signal,
  }

  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
      opts.body = body
    } else {
      opts.headers['Content-Type'] = 'application/json'
      opts.body = JSON.stringify(body)
    }
  }

  const res = await fetch(url + qs, opts)
  const ct = res.headers.get('content-type') || ''
  const payload = ct.includes('application/json') ? await res.json().catch(() => null) : await res.text()

  if (!res.ok) throw new APIError(res.status, payload, `${method} ${url} → ${res.status}`)
  return payload
}

export const CAL = {
  get:    (url, opts)       => request('GET',    url, opts),
  post:   (url, body, opts) => request('POST',   url, { ...opts, body }),
  put:    (url, body, opts) => request('PUT',    url, { ...opts, body }),
  patch:  (url, body, opts) => request('PATCH',  url, { ...opts, body }),
  delete: (url, opts)       => request('DELETE', url, opts),

  // ── Convenience wrappers ────────────────────────
  me:         ()                      => CAL.get('/api/me'),
  logout:     ()                      => CAL.post('/api/auth/logout'),
  list:       (path)                  => CAL.get('/api/files', { query: { path } }),
  tree:       (path = '/', depth = 2) => CAL.get('/api/files/tree', { query: { path, depth } }),
  text:       (path, bytes = 65536)   => CAL.get('/api/text', { query: { path, bytes } }),
  storage:    ()                      => CAL.get('/api/storage'),
  search:     (q, { path = '/', limit = 50, signal } = {}) =>
    CAL.get('/api/search', { query: { q, path, limit }, signal }),

  credentials:         ()              => CAL.get('/api/credentials'),
  credentialsRename:   (id, label)     => CAL.patch(`/api/credentials/${id}`, { label }),
  credentialsRevoke:   (id)            => CAL.delete(`/api/credentials/${id}`),
  mkdir:      (path, name)            => CAL.post('/api/files/mkdir', { path, name }),
  move:       (src, dst_parent, name) => CAL.post('/api/files/move', { src, dst_parent, name }),
  rename:     (path, name)            => CAL.post('/api/files/rename', { path, name }),
  remove:     (path)                  => CAL.delete('/api/files', { query: { path } }),
  stats:      ()                      => CAL.get('/api/stats'),
  share:      (body)                  => CAL.post('/api/share', body),

  // Login options → JSON con challenge
  loginOptions:    () => CAL.post('/api/auth/login/options'),
  loginVerify:     (body) => CAL.post('/api/auth/login/verify', body),
  registerOptions: (body = {}) => CAL.post('/api/auth/register/options', body),
  registerVerify:  (body) => CAL.post('/api/auth/register/verify', body),

  // Chunked PUT upload — raw octet-stream + Content-Range.
  uploadChunk(parent, name, blob, { offset, total, signal } = {}) {
    return request('PUT', '/api/files/upload', {
      body: blob,
      signal,
      headers: {
        'Content-Type':  'application/octet-stream',
        'Content-Range': `bytes ${offset}-${offset + blob.size - 1}/${total}`,
      },
      query: { path: parent, name },
    })
  },
}

export default CAL
